import React, { useState, useRef, useCallback } from 'react';

import { debounce } from 'lodash';

import Button from 'components/shared/Button';

import type { Project, ProjectSaveParams } from 'domains/projects';

type EditProjectNameProps = {
  project: Project;
  onSave: (project: ProjectSaveParams) => void;
};

function EditProjectName(props: EditProjectNameProps) {
  const { project, onSave } = props;
  const [isEditing, setIsEditing] = useState(false);
  const nameInput = useRef<HTMLInputElement>(null);
  const descInput = useRef<HTMLTextAreaElement>(null);
  const debounceInputChange = debounce(saveCurrentProjectValues, 2000);

  const handleEditClick = useCallback(() => {
    setIsEditing(true);
    // unfortunate hack to get around react's rendering cycle
    setTimeout(() => {
      if (nameInput.current) {
        nameInput.current.focus();
      }
    }, 50);
  }, [setIsEditing]);

  function handleInputBlur(e: React.FocusEvent) {
    debounceInputChange.cancel();

    if (e.relatedTarget === nameInput.current || e.relatedTarget === descInput.current) {
      return;
    }

    saveCurrentProjectValues();
    setIsEditing(false);
  }

  function saveCurrentProjectValues() {
    if (!nameInput.current || nameInput.current.value === '' || !descInput.current) return;

    const nameValue = nameInput.current.value;
    const descValue = descInput.current.value;

    if (nameValue !== project.name || descValue !== project.description) {
      onSave({ guid: project.guid, id: project.id, name: nameValue, description: descValue });
    }
  }

  return (
    <div className={`save-project-info ${isEditing ? 'is-editing' : ''}`}>
      <div className="edit-project-details">
        <Button
          type="icon"
          icon="edit"
          a11yText="Edit Project Name and Description"
          appearance="light"
          onClick={handleEditClick}
        />
        <div>
          <p>{project.name}</p>
          <p>{project.description}</p>
        </div>
      </div>

      <div className="edit-project-inputs" hidden={!isEditing}>
        <label className="screen-reader-text" htmlFor="save_project_name-inline">
          Project Name
        </label>
        <input
          type="text"
          id="save_project_name-inline"
          ref={nameInput}
          name="save-project-name"
          defaultValue={project.name || ''}
          onChange={debounceInputChange}
          onBlur={handleInputBlur}
        />
        <label className="screen-reader-text" htmlFor="save_project_description-inline">
          Project Description
        </label>
        <textarea
          id="save_project_description-inline"
          rows={1}
          ref={descInput}
          name="save-project-description-inline"
          defaultValue={project.description || undefined}
          onChange={debounceInputChange}
          onBlur={handleInputBlur}
        />
      </div>
    </div>
  );
}

export default EditProjectName;
